import ICUangBelanja from 'assets/icons/uang-belanja-icon.svg'
import BreadCrumbs from 'components/breadcrumbs'
import CardBankAccount from 'components/CardBankAccount'
import LoadingV2 from 'components/LoadingV2'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import useIsMobile from 'hooks/useIsMobile'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { Col, Row } from 'reactstrap'
import { BUSINESS_TOPUP, MANAGE } from 'utils/subjectActions'
import { useFetchSaldoQuery } from '../dashboard/dashboardApiSlice'
import DisbursementModal from './DisbursementModal'
import {
  useFetchDisburseHistoryListQuery,
  useFetchTeamListQuery,
} from './employeePaymentsListApiSlice'
import {
  setActiveTab,
  setBatchName,
  setDisbursementDataDisburses,
  setHistoryFetchOption,
} from './employeePaymentsListSlice'
import FooterConfirmation from './FooterConfirmation'
import './styles.css'
import TabDisbursement from './TabDisbursement'
import TabHistory from './TabHistory'

const transformTeamToDisburses = (teamData) => {
  if (!teamData) return null

  return {
    batchName: '',
    disburses: teamData?.map((item) => ({
      unit: 0,
      employeeUserId: item.userId,
      employeeName: item.name,
      nameItem: '',
      prIds: '',
      itemId: null,
      amount: 0,
      disburseMethod: '',
      notes: '',
      proofImgs: '',
      processreportIds: [],
      rolesName: item.rolesName,
    })),
  }
}

const EmployeePayments = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const profile = useOutletContext()
  const accessTopup = useAbilityAccess({
    action: MANAGE,
    subject: BUSINESS_TOPUP,
  })
  const isMobile = useIsMobile()
  const location = useLocation()

  useEffect(() => {
    if (location.pathname === '/employee-payments') {
      dispatch(setActiveTab('disbursement'))
    }
  }, [location.pathname, dispatch])

  const {
    data: dataSaldo,
    isLoading: isLoadingSaldo,
    isSuccess,
    isError,
  } = useFetchSaldoQuery()

  const {
    tag,
    activeTab,
    historyTag,
    disbursementData,
    isOpenModalManual,
    employeeEdit,
  } = useSelector((state) => state.employeePaymentsList)

  const { data: disburseHistoryData, isLoading: isLoadingHistory } =
    useFetchDisburseHistoryListQuery(
      {
        page: historyTag?.page,
        size: historyTag?.size,
        sortby: historyTag?.sortby,
        sortdir: historyTag?.sortdir,
        s: historyTag?.search,
      },
      { refetchOnMountOrArgChange: true, skip: activeTab !== 'history' },
    )

  const { data, isLoading, refetch, isFetching } = useFetchTeamListQuery(
    {
      page: tag?.page,
      size: tag?.size,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
      s: tag?.search,
    },
    { refetchOnMountOrArgChange: true, skip: false },
  )

  const disbursementDataNew = data?.items
    ? transformTeamToDisburses(data.items)
    : []

  useEffect(() => {
    if (
      (!disbursementData?.disburses ||
        disbursementData.disburses.length === 0) &&
      disbursementDataNew
    ) {
      dispatch(setBatchName(disbursementDataNew.batchName))
      dispatch(setDisbursementDataDisburses(disbursementDataNew.disburses))
    }
  }, [disbursementData, disbursementDataNew])

  if (isLoading || isFetching) {
    return <LoadingV2 />
  }

  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab))
  }

  const totalAmount = disbursementData?.disburses?.reduce(
    (total, disburses) => total + disburses.amount,
    0,
  )

  return (
    <div className="w-100 px-3">
      <div className="mt-3">
        <BreadCrumbs breadCrumbActive="List of Disbursements" />
      </div>

      {/* HEADER SECTION */}
      <Row className="mb-4 align-items-end g-3">
        <Col xs="12" md="7" lg="8">
          <div className="d-flex flex-column">
            <h5 className="mb-1 fw-bold" style={{ fontSize: '24px', letterSpacing: '-0.5px' }}>Disbursement System</h5>
            <p className="text-muted mb-0" style={{ fontSize: '15px' }}>
              Manage employee payments and disbursements
            </p>
          </div>
        </Col>

        <Col xs="12" md="5" lg="4">
          <CardBankAccount
            iconCard={ICUangBelanja}
            title="Uang Belanja"
            btnTitle="Tambah"
            bankAmount={dataSaldo?.wallet1}
            isLoading={isLoadingSaldo}
            isSuccess={isSuccess}
            isDisable
            isNoButtonTitle
          />
        </Col>
      </Row>

      {/* TABS */}
      <Row className="mb-3">
        <Col xs="12">
          <PMBTabSwitcher
            activeTab={activeTab}
            tabs={[
              { key: 'disbursement', label: 'Disbursement' },
              { key: 'history', label: 'History' },
            ]}
            onTabChange={handleTabChange}
            className="d-flex gap-3"
            testId="emp-pay-tab"
          />
        </Col>
      </Row>

      {/* TAB CONTENT */}
      <DisbursementModal isOpen={isOpenModalManual} employee={employeeEdit} />

      <Row>
        <Col xs="12">
          {activeTab === 'disbursement' && (
            <>
              <TabDisbursement
                isLoading={isLoading}
                isFetching={isFetching}
                disburses={disbursementDataNew?.disburses || []}
              />

              {totalAmount > 0 && <FooterConfirmation />}
            </>
          )}

          {activeTab === 'history' && (
            <>
              <TabHistory
                isLoading={isLoadingHistory}
                isFetching={isFetching}
                dataHistory={disburseHistoryData?.items || []}
                totalItems={disburseHistoryData?.totalItems || 0}
                currentPage={historyTag?.page || 0}
                rowsPerPage={historyTag?.size || 10}
                setCurrentPage={(page) => dispatch(setHistoryFetchOption({ page }))}
                setRowsPerPage={(size) => dispatch(setHistoryFetchOption({ size, page: 0 }))}
              />
            </>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default EmployeePayments
